import { FC, useCallback, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import __ from "src/utils/Translations";
import {
  CalendarListCategory,
  ICalendar,
  ISortedCalendars,
} from "./Calendars.models";
import CalendarsListItem from "./CalendarsListItem";

interface ICalendarsListProps {
  data: ISortedCalendars;
  category: string;
  isFetching: boolean;
}

const listHeadings = {
  [CalendarListCategory.PAST]: "Kalendarze z lat minionych",
  [CalendarListCategory.PRESENT]: "Kalendarze na bieżący rok",
  [CalendarListCategory.FUTURE]: "Kalendarze na lata przyszłe",
};

const CalendarsListCategory: FC<ICalendarsListProps> = ({
  data,
  category,
  isFetching,
}) => {
  const [expanded, setExpanded] = useState<string[]>([]);

  const sortedEntries = useMemo(() => {
    const entries = Object.entries(data);

    return entries.sort(
      ([yearA], [yearB]) => parseInt(yearB, 10) - parseInt(yearA, 10)
    );
  }, [data]);

  const handleToggleAll = useCallback(() => {
    if (expanded.length) {
      setExpanded([]);
    } else {
      setExpanded(Object.keys(data));
    }
  }, [data, expanded.length]);

  const handleAccordionChange = useCallback(
    (year: string) => {
      if (expanded.includes(year)) {
        setExpanded((prevState) => prevState.filter((item) => item !== year));
      } else {
        setExpanded((prevState) => [...prevState, year]);
      }
    },
    [expanded]
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Typography
          variant="body2"
          component="h3"
          color="textSecondary"
          m="2rem 0"
        >
          {__(listHeadings[category as CalendarListCategory])}
        </Typography>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link component="button" color="textPrimary" onClick={handleToggleAll}>
          {__("Rozwiń wszystkie")}
        </Link>
      </Stack>
      {sortedEntries.map(([year, calendars]) => (
        <Accordion
          key={year}
          variant="nestedListAccordion"
          expanded={expanded.includes(year)}
          onChange={() => handleAccordionChange(year)}
        >
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon />}
            sx={{
              borderBottom: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography variant="subtitle1" component="h4" margin="0">
              {__("Lista kalendarzy")} {year}
              {category === CalendarListCategory.FUTURE && (
                <Chip
                  variant="outlined"
                  size="small"
                  color="primary"
                  label={__("Przyszły kalendarz")}
                  sx={{
                    fontWeight: 400,
                    ml: "1rem",
                  }}
                />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails data-testid="absences-calendars-list">
            {calendars.map((calendar: ICalendar) => (
              <CalendarsListItem calendar={calendar} isFetching={isFetching} />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
      <Divider sx={{ mt: "2rem" }} />
    </>
  );
};

export default CalendarsListCategory;
